import { useState, useEffect } from 'react';
import { cricclubsAPI, seriesAPI } from '../../services/api';
import { PageHeader, SectionTitle, Field, Spinner } from '../../components/ui';
import toast from 'react-hot-toast';

export default function CricClubsPage() {
  const [method, setMethod] = useState('scraper');
  const [status, setStatus] = useState(null);
  const [testing, setTesting] = useState(false);
  const [pulling, setPulling] = useState(false);
  const [pushing, setPushing] = useState(false);
  const [series, setSeries] = useState([]);
  const [selectedSeries, setSelectedSeries] = useState({ cricclubs: '', local: '' });
  const [apiKey] = useState(!!process.env.REACT_APP_API_KEY_CONFIGURED);

  useEffect(() => {
    cricclubsAPI.status().then(r => setStatus(r.data)).catch(() => {});
    seriesAPI.list().then(r => setSeries(r.data)).catch(() => {});
  }, []);

  const testConnection = async () => {
    setTesting(true);
    try {
      const { data } = await cricclubsAPI.test(method);
      if (data.success) toast.success(`Connection OK (${method === 'api' ? 'API' : 'Scraper'} method)`);
      else toast.error(data.error || 'Connection failed');
    } catch (err) {
      toast.error(err.response?.data?.error || 'Test failed');
    } finally { setTesting(false); }
  };

  const doPull = async () => {
    if (!selectedSeries.cricclubs || !selectedSeries.local) {
      return toast.error('Select both a CricClubs series and a local series');
    }
    setPulling(true);
    try {
      await cricclubsAPI.pull({ method, cricclubs_series_id: selectedSeries.cricclubs, local_series_id: selectedSeries.local });
      toast.success('Sync started in background — check status in a moment');
      setTimeout(() => cricclubsAPI.status().then(r => setStatus(r.data)), 5000);
    } catch (err) {
      toast.error(err.response?.data?.error || 'Pull failed');
    } finally { setPulling(false); }
  };

  const doPush = async () => {
    if (!selectedSeries.local) return toast.error('Select a local series to push');
    if (method !== 'api') return toast.error('Push requires Method 1 (API). Set your API key in settings.');
    setPushing(true);
    try {
      await cricclubsAPI.push(selectedSeries.local);
      toast.success('Fixtures pushed to CricClubs!');
    } catch (err) {
      toast.error(err.response?.data?.error || 'Push failed');
    } finally { setPushing(false); }
  };

  return (
    <div>
      <PageHeader icon="⟳" title="CricClubs Integration"
        subtitle="Sync teams, players, rankings and fixtures with cricclubs.com/MichCA"
        actions={
          <>
            <button className="btn btn-ghost" onClick={testConnection} disabled={testing}>
              {testing ? <><Spinner size={14} /> Testing...</> : '⚡ Test Connection'}
            </button>
            <button className="btn btn-primary" onClick={doPull} disabled={pulling}>
              {pulling ? <><Spinner size={14} /> Pulling...</> : '↓ Pull Data'}
            </button>
          </>
        }
      />

      {/* Last sync status bar */}
      {status?.last_sync && (
        <div className="snote" style={{ marginBottom: 20, display: 'flex', alignItems: 'center', gap: 12 }}>
          <div style={{ width: 8, height: 8, borderRadius: '50%', background: 'var(--accent)', flexShrink: 0 }} />
          <span>Last sync: <strong>{new Date(status.last_sync.timestamp).toLocaleString()}</strong> via {status.last_sync.method} — {status.last_sync.records_synced} records — <span className={`tag ${status.last_sync.status === 'success' ? 'tag-green' : 'tag-gold'}`}>{status.last_sync.status}</span></span>
        </div>
      )}

      {/* ── Integration Method Selector ── */}
      <div className="card" style={{ padding: 20, marginBottom: 20 }}>
        <SectionTitle>Integration Method</SectionTitle>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: 20 }}>

          {/* Method 1 — API */}
          <div
            onClick={() => setMethod('api')}
            style={{
              padding: 20, borderRadius: 10, cursor: 'pointer',
              border: `2px solid ${method === 'api' ? 'var(--accent)' : 'var(--border)'}`,
              background: method === 'api' ? 'rgba(0,200,150,.07)' : 'var(--bg)',
              transition: 'all .15s'
            }}
          >
            <div style={{ display: 'flex', alignItems: 'flex-start', gap: 10, marginBottom: 12 }}>
              <div style={{ width: 18, height: 18, borderRadius: '50%', border: `2px solid ${method === 'api' ? 'var(--accent)' : 'var(--border)'}`, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, marginTop: 2 }}>
                {method === 'api' && <div style={{ width: 8, height: 8, borderRadius: '50%', background: 'var(--accent)' }} />}
              </div>
              <div>
                <div style={{ fontWeight: 800, fontSize: 15, color: method === 'api' ? 'var(--accent)' : 'var(--text)' }}>
                  Method 1 — Official API
                </div>
                <span className="tag tag-gold" style={{ marginTop: 4 }}>Requires API Key</span>
              </div>
            </div>
            <ul style={{ fontSize: 12, color: 'var(--mid)', lineHeight: 1.8, paddingLeft: 18 }}>
              <li>Authenticated REST API calls</li>
              <li>↓ Pull: teams, players, standings, grounds, results</li>
              <li>↑ Push: new fixtures back to CricClubs</li>
              <li>Most reliable — recommended when API access available</li>
              <li style={{ color: status?.api_key_configured ? 'var(--accent)' : 'var(--red)' }}>
                {status?.api_key_configured ? '✓ API key configured' : '✗ API key not set in .env'}
              </li>
            </ul>
          </div>

          {/* Method 2 — Scraper */}
          <div
            onClick={() => setMethod('scraper')}
            style={{
              padding: 20, borderRadius: 10, cursor: 'pointer',
              border: `2px solid ${method === 'scraper' ? 'var(--blue)' : 'var(--border)'}`,
              background: method === 'scraper' ? 'rgba(59,130,246,.07)' : 'var(--bg)',
              transition: 'all .15s'
            }}
          >
            <div style={{ display: 'flex', alignItems: 'flex-start', gap: 10, marginBottom: 12 }}>
              <div style={{ width: 18, height: 18, borderRadius: '50%', border: `2px solid ${method === 'scraper' ? 'var(--blue)' : 'var(--border)'}`, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, marginTop: 2 }}>
                {method === 'scraper' && <div style={{ width: 8, height: 8, borderRadius: '50%', background: 'var(--blue)' }} />}
              </div>
              <div>
                <div style={{ fontWeight: 800, fontSize: 15, color: method === 'scraper' ? 'var(--blue)' : 'var(--text)' }}>
                  Method 2 — Public Site Scraper
                </div>
                <span className="tag tag-green" style={{ marginTop: 4 }}>No API Key Needed</span>
              </div>
            </div>
            <ul style={{ fontSize: 12, color: 'var(--mid)', lineHeight: 1.8, paddingLeft: 18 }}>
              <li>Reads from public cricclubs.com/MichCA pages</li>
              <li>↓ Pull: team names, standings, results, grounds</li>
              <li>↗ Push: not available (read-only)</li>
              <li>Includes crawl delay ({process.env.REACT_APP_SCRAPER_DELAY || 2}s) to be respectful</li>
              <li style={{ color: 'var(--accent)' }}>✓ Available immediately — no setup</li>
            </ul>
          </div>
        </div>

        {/* API Key warning */}
        {method === 'api' && !status?.api_key_configured && (
          <div className="wnote" style={{ marginBottom: 16 }}>
            ⚠ <strong>API key not configured.</strong> Add <code>CRICCLUBS_API_KEY=your_key</code> to the backend <code>.env</code> file and restart the server. Contact CricClubs support or your club admin to obtain an API key.
          </div>
        )}

        {/* Push warning for scraper */}
        {method === 'scraper' && (
          <div className="anote" style={{ marginBottom: 16 }}>
            ℹ Method 2 is <strong>pull-only</strong>. To push fixtures back to CricClubs, switch to Method 1 (API) and configure your API key.
          </div>
        )}
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20, marginBottom: 20 }}>
        {/* Pull config */}
        <div className="card" style={{ padding: 20 }}>
          <SectionTitle>Pull Configuration</SectionTitle>
          <Field label="CricClubs Series ID" note="The numeric ID from the CricClubs URL: ?series_id=XXX">
            <input className="input" placeholder="e.g. 113" value={selectedSeries.cricclubs}
              onChange={e => setSelectedSeries(p => ({ ...p, cricclubs: e.target.value }))} />
          </Field>
          <Field label="Match to Local Series">
            <select className="input select" value={selectedSeries.local}
              onChange={e => setSelectedSeries(p => ({ ...p, local: e.target.value }))}>
              <option value="">— Select local series —</option>
              {series.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
            </select>
          </Field>
          <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
            <button className="btn btn-primary" onClick={doPull} disabled={pulling} style={{ flex: 1, justifyContent: 'center' }}>
              {pulling ? <><Spinner size={14} /> Pulling...</> : '↓ Pull Now'}
            </button>
            <button className="btn btn-ghost" onClick={testConnection} disabled={testing}>
              {testing ? '...' : 'Test'}
            </button>
          </div>
        </div>

        {/* Push config — API only */}
        <div className="card" style={{ padding: 20 }}>
          <SectionTitle color="var(--blue)">Push Fixtures (API Only)</SectionTitle>
          {method !== 'api' ? (
            <div className="anote">Switch to Method 1 (API) to enable pushing fixtures back to CricClubs.</div>
          ) : (
            <>
              <Field label="Local Series to Push">
                <select className="input select" value={selectedSeries.local}
                  onChange={e => setSelectedSeries(p => ({ ...p, local: e.target.value }))}>
                  <option value="">— Select series —</option>
                  {series.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
                </select>
              </Field>
              <p className="note" style={{ marginBottom: 12 }}>Only confirmed fixtures will be pushed. Ensure all teams have their CricClubs Team ID set.</p>
              <button className="btn btn-gold" onClick={doPush} disabled={pushing} style={{ width: '100%', justifyContent: 'center' }}>
                {pushing ? <><Spinner size={14} /> Pushing...</> : '↑ Push Fixtures to CricClubs'}
              </button>
            </>
          )}
        </div>
      </div>

      {/* What can be synced */}
      <div className="card" style={{ padding: 20, marginBottom: 20 }}>
        <SectionTitle>Data Sync Capabilities</SectionTitle>
        <div style={{ overflowX: 'auto' }}>
          <table className="data-table">
            <thead>
              <tr>
                <th>Data Type</th>
                <th>Method 1 — API Pull</th>
                <th>Method 1 — API Push</th>
                <th>Method 2 — Scraper Pull</th>
              </tr>
            </thead>
            <tbody>
              {[
                ['Team names & short codes',     true,  false, true],
                ['Player names & CricClubs IDs', true,  false, false],
                ['Player profile photos',        true,  false, false],
                ['Previous season standings',    true,  false, true],
                ['Ground names & locations',     true,  false, true],
                ['Match results',                true,  true,  true],
                ['New 2026 fixtures',            false, true,  false],
              ].map(([item, ap, apush, sc]) => (
                <tr key={item}>
                  <td>{item}</td>
                  <td>{ap ? <span className="tag tag-green">↓ Available</span> : <span className="tag tag-red">✗</span>}</td>
                  <td>{apush ? <span className="tag tag-blue">↑ Available</span> : <span className="tag tag-red">✗</span>}</td>
                  <td>{sc ? <span className="tag tag-green">↓ Available</span> : <span className="tag tag-red">✗</span>}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Sync history */}
      {status?.history?.length > 0 && (
        <div className="card" style={{ padding: 20 }}>
          <SectionTitle color="var(--mid)">Sync History</SectionTitle>
          <table className="data-table">
            <thead><tr><th>Time</th><th>Type</th><th>Method</th><th>Records</th><th>Status</th><th>Errors</th></tr></thead>
            <tbody>
              {status.history.map(h => (
                <tr key={h.id}>
                  <td style={{ fontSize: 12 }}>{new Date(h.started_at).toLocaleString()}</td>
                  <td><span className="tag tag-blue">{h.sync_type}</span></td>
                  <td><span className={`tag ${h.method === 'api' ? 'tag-gold' : 'tag-blue'}`}>{h.method}</span></td>
                  <td className="mono">{h.records_synced}</td>
                  <td><span className={`tag ${h.status === 'success' ? 'tag-green' : h.status === 'partial' ? 'tag-gold' : 'tag-red'}`}>{h.status}</span></td>
                  <td style={{ fontSize: 12, color: 'var(--mid)' }}>{Array.isArray(h.errors) ? h.errors.length : 0} errors</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
